'use client'

import { useEffect, useState } from 'react'
import { KeyRound, Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { getTournamentChatId, isChatMember, getLobbyCode } from '@/lib/tournament-chat'
import { getMyTournamentBan } from '@/lib/tournament-registration'

/** The lobby code unlocks this many ms before the tournament starts. */
const LOBBY_UNLOCK_LEAD_MS = 5 * 60 * 1000

/** Resolves the viewer's lobby-code standing: membership, code, and unlock state. */
export function useLobbyCode(tournamentId: string, startsAt: string) {
  const { session } = useAuth()
  const userId = session?.user?.id ?? null
  const [isMember, setIsMember] = useState(false)
  const [lobbyCode, setLobbyCode] = useState<string | null>(null)
  const [unlocked, setUnlocked] = useState(false)
  const [chatId, setChatId] = useState<string | null>(null)
  const [banned, setBanned] = useState(false)

  // A host can ban the viewer from this event. RLS already refuses them the
  // code, so resolve the ban to explain that instead of counting down to an
  // unlock that will never produce one.
  useEffect(() => {
    if (!userId) {
      setBanned(false)
      return
    }
    let cancelled = false
    getMyTournamentBan(tournamentId, userId).then((ban) => {
      if (!cancelled) setBanned(!!ban)
    })
    return () => {
      cancelled = true
    }
  }, [tournamentId, userId])

  // Membership: whether the viewer has joined this tournament's chat.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const cid = await getTournamentChatId(tournamentId)
      const member = cid && userId ? await isChatMember(cid, userId) : false
      if (!cancelled) {
        setChatId(cid)
        setIsMember(member)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tournamentId, userId])

  // Unlock state: flips on once we're within the lead window of the start.
  useEffect(() => {
    const unlockAt = new Date(startsAt).getTime() - LOBBY_UNLOCK_LEAD_MS
    const check = () => {
      const open = Date.now() >= unlockAt
      setUnlocked(open)
      return open
    }
    if (check()) return
    const id = setInterval(() => {
      if (check()) clearInterval(id)
    }, 30_000)
    return () => clearInterval(id)
  }, [startsAt])

  // The code is only readable (RLS) within the lead window, so fetch it once
  // we're both a member and unlocked — there is nothing to get before then.
  useEffect(() => {
    if (!isMember || !unlocked || banned) {
      setLobbyCode(null)
      return
    }
    let cancelled = false
    ;(async () => {
      const code = await getLobbyCode(tournamentId)
      if (!cancelled) setLobbyCode(code)
    })()
    return () => {
      cancelled = true
    }
  }, [isMember, unlocked, banned, tournamentId])

  return { isMember, lobbyCode, unlocked, chatId, banned }
}

/** Break ms remaining into days / hours / minutes / seconds. */
function splitCountdown(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000))
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  }
}

/** Spoken "2d 14h 3m 9s" form for the screen-reader live region. */
function spokenCountdown(ms: number) {
  const { days, hours, minutes, seconds } = splitCountdown(ms)
  return `${days}d ${hours}h ${minutes}m ${seconds}s`
}

/** A single labelled "12 / Hrs" cell in the days/hours/mins/secs countdown. */
function CountdownCell({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="font-mono text-2xl font-bold tabular-nums leading-none text-foreground">
        {String(value).padStart(2, '0')}
      </span>
      <span className="mt-1 text-[9px] font-medium uppercase tracking-[0.15em] text-muted-foreground">
        {label}
      </span>
    </div>
  )
}

/**
 * The lobby-code card. Until 5 min before start it shows a live countdown to
 * the unlock; from then on it reveals the code with a copy button.
 */
export function LobbyCodeCard({
  lobbyCode,
  unlocked,
  startsAt,
  banned = false,
  className,
}: {
  lobbyCode: string | null
  unlocked: boolean
  startsAt: string
  /** Host banned the viewer from this event: no code, ever. */
  banned?: boolean
  className?: string
}) {
  const [copied, setCopied] = useState(false)
  const [remaining, setRemaining] = useState<number | null>(null)

  // Tick the countdown to the unlock time once a second while still locked.
  useEffect(() => {
    if (unlocked) return
    const unlockAt = new Date(startsAt).getTime() - LOBBY_UNLOCK_LEAD_MS
    const tick = () => setRemaining(unlockAt - Date.now())
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [unlocked, startsAt])

  // Reveal only once the code has actually arrived. The countdown can hit zero
  // a moment before the (server-gated) code is fetched, so guard on lobbyCode
  // to avoid flashing an empty value.
  const open = (unlocked || (remaining !== null && remaining <= 0)) && !!lobbyCode

  const copy = async () => {
    if (!lobbyCode) return
    try {
      await navigator.clipboard.writeText(lobbyCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable */
    }
  }

  // A banned viewer never gets the code, so counting down to the unlock would
  // be a promise the server won't keep. Say so plainly instead.
  if (banned) {
    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border bg-card/60 p-5',
          className
        )}
      >
        <div className="flex min-w-0 items-center gap-3.5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted/50 ring-1 ring-border">
            <KeyRound className="size-5 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Lobby code
            </div>
            <div className="mt-0.5 text-sm font-semibold text-foreground">
              Not available
            </div>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          The host has banned you from this event, so the lobby code won&apos;t
          be shared with you. Reach out to them if you think this was a mistake.
        </p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        '@container relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/15 via-card to-card p-5',
        className
      )}
    >
      <div className="pointer-events-none absolute -right-10 -top-10 size-36 rounded-full bg-primary/20 blur-3xl" />
      <div className="relative flex flex-col gap-3 @sm:flex-row @sm:items-center @sm:justify-between @sm:gap-4">
        <div className="flex min-w-0 items-center gap-3.5">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 ring-1 ring-primary/30">
            <KeyRound className="size-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/80">
              Lobby code
            </div>
            {open ? (
              <div className="mt-0.5 truncate font-mono text-2xl font-bold tracking-[0.22em] text-foreground">
                {lobbyCode}
              </div>
            ) : remaining === null ? (
              <div className="mt-0.5 font-mono text-2xl font-bold text-foreground">—</div>
            ) : (
              (() => {
                const { days, hours, minutes, seconds } = splitCountdown(remaining)
                return (
                  <div className="mt-1 flex items-end gap-2">
                    <CountdownCell value={days} label="Days" />
                    <span className="pb-3.5 font-mono text-xl font-bold text-muted-foreground/50">:</span>
                    <CountdownCell value={hours} label="Hrs" />
                    <span className="pb-3.5 font-mono text-xl font-bold text-muted-foreground/50">:</span>
                    <CountdownCell value={minutes} label="Min" />
                    <span className="pb-3.5 font-mono text-xl font-bold text-muted-foreground/50">:</span>
                    <CountdownCell value={seconds} label="Sec" />
                  </div>
                )
              })()
            )}
          </div>
        </div>
        {open && (
          <button
            onClick={copy}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:brightness-110 active:scale-95 @sm:w-auto @sm:shrink-0 @sm:justify-start"
          >
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>
      <p className="relative mt-3 text-xs text-muted-foreground">
        {open
          ? 'Share this with your squad to enter the match lobby.'
          : 'The lobby code unlocks 5 minutes before the tournament starts.'}
      </p>
      {!open && (
        <p className="sr-only" aria-live="polite">
          Lobby code unlocks in {remaining === null ? 'a few minutes' : spokenCountdown(remaining)}
        </p>
      )}
    </div>
  )
}

/**
 * Slim sticky copy of the lobby code for screens without the sidebar (below
 * `lg`). Rendered by the event page while the in-feed card is scrolled away.
 */
export function LobbyCodeBar({
  lobbyCode,
  unlocked,
  startsAt,
  banned = false,
  className,
}: {
  lobbyCode: string | null
  unlocked: boolean
  startsAt: string
  /** Host banned the viewer: hide the bar rather than tease a countdown. */
  banned?: boolean
  className?: string
}) {
  const [copied, setCopied] = useState(false)
  const [remaining, setRemaining] = useState<number | null>(null)

  useEffect(() => {
    if (unlocked) return
    const unlockAt = new Date(startsAt).getTime() - LOBBY_UNLOCK_LEAD_MS
    const tick = () => setRemaining(unlockAt - Date.now())
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [unlocked, startsAt])

  const open = (unlocked || (remaining !== null && remaining <= 0)) && !!lobbyCode

  // Banned viewers never get a code; the slim bar has no room to explain, and
  // the in-feed card already does. Render nothing.
  if (banned) return null

  const copy = async () => {
    if (!lobbyCode) return
    try {
      await navigator.clipboard.writeText(lobbyCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 border-b border-primary/30 bg-card/90 px-4 py-2.5 backdrop-blur-xl',
        className
      )}
    >
      <KeyRound className="size-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/80">
          Lobby code
        </div>
        <div className="truncate font-mono text-base font-bold tracking-[0.18em]">
          {open
            ? lobbyCode
            : remaining === null
              ? '—'
              : `unlocks in ${spokenCountdown(remaining)}`}
        </div>
      </div>
      {open && (
        <button
          onClick={copy}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-all hover:brightness-110 active:scale-95"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      )}
    </div>
  )
}
